import type {FC} from 'react';
import {
  Box,
  Divider,
  MarkdownViewer,
  Stack,
  Typography,
} from '@wellstone-solutions/web';
import {Theme} from '@wellstone-solutions/common';
import type {UIResourceType} from '@wellstone-solutions/common/models/rest';
import {useContactMethods} from '../../hooks';
import {ContactMethod} from '../ContactMethod';

type ContactMethodItem = {iconName: string; text: string};

interface FlagResourceSummaryProps {
  resource: UIResourceType;
}

// Read-only details so the reporter can see what they're flagging.
// No links or category filters, unlike ResourceDetailView.
export const FlagResourceSummary: FC<FlagResourceSummaryProps> = ({
  resource,
}) => {
  const {contactMethods} = useContactMethods(resource);

  return (
    <Stack spacing={2} data-testid="flag-resource-summary">
      <Box>
        <Typography sx={styles.title}>{resource.title}</Typography>
        {!!resource.subtitle && (
          <Typography sx={styles.subtitle}>{resource.subtitle}</Typography>
        )}
      </Box>

      {!!resource.description && (
        <Box sx={styles.description}>
          <MarkdownViewer value={resource.description} />
        </Box>
      )}

      {!!resource.dateTimeInformation && (
        <Typography sx={styles.dateTimeText}>
          {resource.dateTimeInformation}
        </Typography>
      )}

      {contactMethods.length > 0 && (
        <Stack sx={styles.contactMethods} spacing={1.5} divider={<Divider />}>
          {contactMethods.map(({iconName, text}: ContactMethodItem) => (
            <ContactMethod key={text} iconName={iconName} text={text} />
          ))}
        </Stack>
      )}
    </Stack>
  );
};

const styles = {
  title: {
    color: Theme.colorPalette.onSurface,
    fontSize: 20,
    lineHeight: '24px',
  },
  subtitle: {
    mt: 0.5,
    fontSize: 15,
    color: Theme.colorPalette.onSurface,
  },
  description: {
    fontSize: 14,
    '& p': {m: 0},
  },
  dateTimeText: {
    fontSize: 14,
    color: Theme.colorPalette.dark,
  },
  contactMethods: {
    backgroundColor: Theme.colorPalette.secondary4,
    p: 2,
    borderRadius: 3,
  },
};
